// Реестр перехватчиков аппаратной кнопки «Назад» для ПОЛНОЭКРАННЫХ режимов.
//
// ПОЧЕМУ ОТДЕЛЬНЫЙ МЕХАНИЗМ, А НЕ `role="dialog"`.
// `useOverlayBackGuard` ищет открытые оверлеи по разметке
// (`[role=dialog][data-state=open]`, `[data-vaul-drawer]`, `[role=menu]`) и шлёт
// им Escape. Полноэкранные режимы — запись лекции, голосовой звонок, онбординг —
// это НЕ оверлеи, а экраны: они занимают весь вьюпорт, живут в маршруте, под
// ними нет «страницы, к которой можно вернуться закрытием». Приклеить им
// `role="dialog"` было бы хуже, чем оставить как есть: селектор бы совпал,
// guard отправил бы Escape, слушать его было бы некому — и «Назад» перестала
// бы работать вовсе, то есть пользователь застрял бы на экране.
//
// Поэтому экран заявляет намерение ЯВНО: «пока я на экране, аппаратная Назад
// значит вот это». Для записи лекции — свернуть (запись продолжается), для
// голосового звонка — открыть подтверждение завершения. Ровно то же, что
// делает крестик в шапке. Ничего не теряется молча.
//
// Порядок в `useOverlayBackGuard`: сначала оверлеи (диалог, открытый ПОВЕРХ
// полноэкранного экрана, обязан закрыться первым), потом перехватчики, потом
// обычный шаг назад по истории.
//
// ОГРАНИЧЕНИЕ. Работает только в Capacitor: браузерную «назад» в мобильном вебе
// перехватить можно лишь записью в `history`, а она затирает `__TSR_index`
// TanStack Router (см. шапку `use-overlay-back-guard.ts`). В мобильном вебе
// поведение прежнее — уход по истории.

/** Возвращает `true`, если перехватчик обработал «Назад» сам. */
export type BackInterceptor = () => boolean;

const interceptors: BackInterceptor[] = [];

/**
 * Регистрирует перехватчик. Возвращает функцию снятия — вызывать в cleanup
 * эффекта. Стек LIFO: последний зарегистрированный получает событие первым
 * (вложенный полноэкранный режим важнее внешнего).
 */
export function registerBackInterceptor(fn: BackInterceptor): () => void {
	interceptors.push(fn);
	return () => {
		const idx = interceptors.lastIndexOf(fn);
		if (idx !== -1) interceptors.splice(idx, 1);
	};
}

/**
 * Прогоняет стек сверху вниз до первого, кто скажет «обработал».
 * Бросок из перехватчика не должен ронять обработку «Назад» целиком —
 * иначе один сломанный экран лишает приложение системной кнопки.
 */
export function runBackInterceptors(): boolean {
	for (let i = interceptors.length - 1; i >= 0; i--) {
		const fn = interceptors[i];
		if (!fn) continue;
		try {
			if (fn()) return true;
		} catch {
			// Перехватчик сломан — считаем необработанным, идём дальше по стеку.
		}
	}
	return false;
}

/** Только для тестов: очистить стек между кейсами. */
export function __resetBackInterceptors(): void {
	interceptors.length = 0;
}
